import React, { useMemo } from "react";
import dayjs from "dayjs";
import isSameOrAfter from "dayjs/plugin/isSameOrAfter";
import isSameOrBefore from "dayjs/plugin/isSameOrBefore";
import { Edit3, Trash2 } from "lucide-react";
import { WEEKDAY_KEYS } from "../../../../constants";

dayjs.extend(isSameOrAfter);
dayjs.extend(isSameOrBefore);
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle
} from "../../../ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow
} from "../../../ui/table";
import { EDSButton } from "../../../eds/EDSButton";
import {
  getBookableAreaNameFromAreaId,
  getZoneNameFormZoneId
} from "../../../../utils/booking";
import { parseDateString } from "../../../../utils/dateUtils";
import { bookableAreasClosed, VenueConfig } from "../../../../types/venues";

interface CloseAreasModalProps {
  venueConfig?: VenueConfig | null;
  date: Date;
  closures: bookableAreasClosed[];
  onEdit?: (closure: bookableAreasClosed) => void;
  onDelete?: (closure: bookableAreasClosed) => void;
  onAddAnother?: () => void;
  onSave?: () => void;
  onClose?: () => void;
}

export const CloseAreasModal = ({
  venueConfig,
  date,
  closures,
  onEdit,
  onDelete,
  onAddAnother,
  onSave,
  onClose
}: CloseAreasModalProps) => {
  const currentDate = dayjs(date);
  // dayjs uses 0=Sunday, 6=Saturday, so we need to adjust for WEEKDAY_KEYS which expects Monday=0
  const dayjsWeekday = currentDate.day(); // 0=Sunday, 1=Monday, ..., 6=Saturday
  const currentDay = WEEKDAY_KEYS[dayjsWeekday === 0 ? 6 : dayjsWeekday - 1]; // Convert to Monday=0 format

  const activeClosures = useMemo(() => {
    if (!closures) return [];

    return closures.filter((closure) => {
      if (closure.date_type === "weekdays" && closure.weekdays) {
        return closure.weekdays[currentDay as keyof typeof closure.weekdays];
      }

      if (closure.date_from && closure.date_to) {
        const dateFrom = parseDateString(closure.date_from).startOf("day");
        const dateTo = parseDateString(closure.date_to).endOf("day");
        const currentDateStart = currentDate.startOf("day");
        return (
          currentDateStart.isSameOrAfter(dateFrom) &&
          currentDateStart.isSameOrBefore(dateTo)
        );
      }

      return false;
    });
  }, [closures, currentDay, currentDate]);

  const getClosureDisplayName = (closure: bookableAreasClosed): string => {
    if (closure.type === "zone" && closure.zone) {
      return getZoneNameFormZoneId(
        closure.zone,
        venueConfig?.bookable_area_zones ?? []
      );
    }

    if (closure.type === "areas" && closure.areas) {
      return closure.areas
        .map((areaId) =>
          getBookableAreaNameFromAreaId(
            areaId,
            venueConfig?.bookable_areas ?? []
          )
        )
        .filter(Boolean)
        .join(", ");
    }

    return "";
  };

  const getClosureTimes = (
    closure: bookableAreasClosed
  ): { fromTime: string; toTime: string } => {
    let fromTime = "All day";
    let toTime = "All day";

    // For weekday-based closures, use time_from and time_to
    if (closure.date_type === "weekdays") {
      fromTime = closure.time_from ?? "All day";
      toTime = closure.time_to ?? "All day";
    } else if (closure.date_from && closure.date_to) {
      // For date-based closures, extract time from the date strings
      const dateFromObj = parseDateString(closure.date_from);
      const dateToObj = parseDateString(closure.date_to);

      // Only set specific times if it's not all day (00:00 to 23:59)
      if (
        !(
          dateFromObj.hour() === 0 &&
          dateFromObj.minute() === 0 &&
          dateToObj.hour() === 23 &&
          dateToObj.minute() === 59
        )
      ) {
        fromTime = dateFromObj.format("HH:mm");
        toTime = dateToObj.format("HH:mm");
      }
    }

    return { fromTime, toTime };
  };

  const handleEditClick = (closure: bookableAreasClosed) => {
    onEdit?.(closure);
  };

  const handleAddAnotherClick = () => {
    onAddAnother?.();
  };

  const handleDeleteClick = (closure: bookableAreasClosed) => {
    onDelete?.(closure);
  };

  return (
    <Dialog open={true} onOpenChange={(open) => !open && onClose?.()}>
      <DialogContent className="max-w-[95vw] lg:max-w-[1000px] max-h-[90vh] min-h-[400px] overflow-hidden flex flex-col">
        <DialogHeader>
          <DialogTitle>Close Areas</DialogTitle>
        </DialogHeader>

        <div className="overflow-auto flex-1">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Type</TableHead>
                <TableHead>Areas</TableHead>
                <TableHead>From</TableHead>
                <TableHead>Until</TableHead>
                <TableHead>Closed Message</TableHead>
                <TableHead>Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {activeClosures.map((closure) => {
                const { fromTime, toTime } = getClosureTimes(closure);

                return (
                  <TableRow key={closure.id}>
                    <TableCell className="capitalize">{closure.type}</TableCell>
                    <TableCell>{getClosureDisplayName(closure)}</TableCell>
                    <TableCell>{fromTime}</TableCell>
                    <TableCell>{toTime}</TableCell>
                    <TableCell>{closure.message ?? ""}</TableCell>
                    <TableCell>
                      <div className="flex gap-2">
                        <EDSButton
                          variant="outline"
                          size="icon"
                          onClick={() => handleEditClick(closure)}
                          aria-label="Edit closure"
                        >
                          <Edit3 size={16} />
                        </EDSButton>
                        <EDSButton
                          variant="outline"
                          size="icon"
                          onClick={() => handleDeleteClick(closure)}
                          aria-label="Delete closure"
                        >
                          <Trash2 size={16} />
                        </EDSButton>
                      </div>
                    </TableCell>
                  </TableRow>
                );
              })}
              {activeClosures.length === 0 && (
                <TableRow>
                  <TableCell
                    colSpan={6}
                    className="p-4 text-center text-muted-foreground"
                  >
                    No active closures at this time
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </div>

        <div className="flex gap-2 justify-end pt-4 border-t">
          <EDSButton
            variant="outline"
            onClick={handleAddAnotherClick}
            style={{ height: "37px" }}
          >
            Add another
          </EDSButton>
          <EDSButton onClick={onSave}>Save</EDSButton>
        </div>
      </DialogContent>
    </Dialog>
  );
};
